import { ref, computed } from 'vue'
import { useUserStore } from '~/stores/user'

// ==================== 配置常量 ====================
const TURNSTILE_STORAGE_KEY = 'turnstile_verification'
const TURNSTILE_TOKEN_CACHE_KEY = 'vp_turnstile_token_cache_v3'
const TURNSTILE_EXPIRY_TIME = 30 * 60 * 1000 // 30 分钟
const AUTO_REFRESH_THRESHOLD = 0.2
const TURNSTILE_HEADER = 'turnstile-token'
const TURNSTILE_RETRY_CODE = 100094

/** 不附加 / 不要求 Turnstile 的接口 */
const TURNSTILE_SKIP_API_PATHS = [
  '/api/aigc/config',
  '/api/aigc/pre_interface/cloudflare/verify',
  '/api/verify-turnstile',
  '/api/aigc/track/log',
  '/nav-logo.webp',
]

/** 匿名用户创建任务：需要人机校验 */
const TURNSTILE_ANON_CREATE_PATH_MARKERS = [
  '/create-task',
  '/create-talking-task',
  '/create-talk-multi-task',
]

/** 注册发验证码：需要人机校验 */
const TURNSTILE_REGISTER_CODE_PATHS = [
  '/lip-sync/api/user/register',
  '/api/user/register',
]

const verificationState = ref(null)
const modalVisible = ref(false)
let pendingGateResolve = null

function normalizePath(path = '') {
  try {
    if (/^https?:\/\//i.test(path)) {
      return new URL(path).pathname
    }
  } catch {
    /* ignore */
  }
  return String(path || '').split('?')[0] || ''
}

const readVerificationFromStorage = () => {
  if (typeof window === 'undefined') return null

  try {
    const stored = localStorage.getItem(TURNSTILE_STORAGE_KEY)
    if (!stored) return null

    const parsed = JSON.parse(stored)
    const timestamp = Number(parsed?.timestamp) || 0
    const token = String(parsed?.token || localStorage.getItem(TURNSTILE_TOKEN_CACHE_KEY) || '')
    const now = Date.now()

    if (timestamp && now - timestamp < TURNSTILE_EXPIRY_TIME) {
      return { timestamp, verified: true, token }
    }

    localStorage.removeItem(TURNSTILE_STORAGE_KEY)
    localStorage.removeItem(TURNSTILE_TOKEN_CACHE_KEY)
    return null
  } catch (error) {
    console.error('Error reading turnstile verification status:', error)
    return null
  }
}

const updateVerificationState = () => {
  verificationState.value = readVerificationFromStorage()
}

function isAnonymousUser() {
  if (typeof window === 'undefined') return false
  try {
    const userStore = useUserStore()
    const token = userStore.token || localStorage.getItem('token') || ''
    return !String(token).trim()
  } catch {
    return !String(localStorage.getItem('token') || '').trim()
  }
}

export function isAnonCreateTaskPath(path = '') {
  const pathname = normalizePath(path)
  return TURNSTILE_ANON_CREATE_PATH_MARKERS.some((marker) => pathname.includes(marker))
}

export function isRegisterCodePath(path = '') {
  const pathname = normalizePath(path)
  return TURNSTILE_REGISTER_CODE_PATHS.some(
    (p) => pathname === p || pathname.endsWith(p),
  )
}

export function shouldSkipTurnstilePath(path = '') {
  const pathname = normalizePath(path)
  return TURNSTILE_SKIP_API_PATHS.some(
    (skip) => pathname === skip || pathname.endsWith(skip),
  )
}

/**
 * 是否需要 CF：
 * 1. 匿名用户创建任务（create-task / create-talking-task / create-talk-multi-task）——缓存 30 分钟复用
 * 2. 注册发验证码（register 接口，含重发）——每次点击都重新校验
 */
export function shouldRequireTurnstilePath(path = '') {
  const pathname = normalizePath(path)
  if (!pathname || shouldSkipTurnstilePath(pathname)) return false

  if (isRegisterCodePath(pathname)) return true

  if (isAnonCreateTaskPath(pathname)) return isAnonymousUser()

  return false
}

export function shouldRetryTurnstileByCode(code) {
  return Number(code) === TURNSTILE_RETRY_CODE
}

export function getTurnstileHeaderName() {
  return TURNSTILE_HEADER
}

/**
 * 管理 Turnstile 验证状态
 */
export const useTurnstile = () => {
  const refreshTimer = ref(null)
  const autoRefreshCallback = ref(null)
  const visibilityHandler = ref(null)
  const storageHandler = ref(null)

  if (typeof window !== 'undefined' && verificationState.value === null) {
    updateVerificationState()
  }

  const isVerified = computed(() => {
    if (typeof window === 'undefined') return false
    return verificationState.value !== null && verificationState.value.verified === true
  })

  const getTurnstileToken = () => {
    if (typeof window === 'undefined') return ''
    if (!isVerified.value) return ''
    return (
      verificationState.value?.token ||
      localStorage.getItem(TURNSTILE_TOKEN_CACHE_KEY) ||
      ''
    )
  }

  const saveVerification = (token = '') => {
    if (typeof window === 'undefined') return

    try {
      const normalizedToken = String(token || '').trim()
      const data = {
        verified: true,
        timestamp: Date.now(),
        ...(normalizedToken ? { token: normalizedToken } : {}),
      }
      localStorage.setItem(TURNSTILE_STORAGE_KEY, JSON.stringify(data))
      if (normalizedToken) {
        localStorage.setItem(TURNSTILE_TOKEN_CACHE_KEY, normalizedToken)
      }

      updateVerificationState()
      setupAutoRefresh()
    } catch (error) {
      console.error('Error saving turnstile verification status:', error)
    }
  }

  const clearVerification = () => {
    if (typeof window === 'undefined') return

    try {
      localStorage.removeItem(TURNSTILE_STORAGE_KEY)
      localStorage.removeItem(TURNSTILE_TOKEN_CACHE_KEY)
      verificationState.value = null
      clearAutoRefresh()
    } catch (error) {
      console.error('Error clearing turnstile verification status:', error)
    }
  }

  const getRemainingTime = computed(() => {
    if (typeof window === 'undefined' || !isVerified.value || !verificationState.value) return 0

    try {
      const { timestamp } = verificationState.value
      const remaining = TURNSTILE_EXPIRY_TIME - (Date.now() - timestamp)
      return Math.max(0, Math.floor(remaining / 1000))
    } catch {
      return 0
    }
  })

  const getRemainingTimePercent = computed(() => {
    if (!isVerified.value) return 0
    const remaining = getRemainingTime.value
    const total = TURNSTILE_EXPIRY_TIME / 1000
    return remaining / total
  })

  const shouldAutoRefresh = computed(() => {
    return isVerified.value && getRemainingTimePercent.value <= AUTO_REFRESH_THRESHOLD
  })

  const setAutoRefreshCallback = (callback) => {
    autoRefreshCallback.value = callback
    setupAutoRefresh()
  }

  const clearAutoRefresh = () => {
    if (!refreshTimer.value) return
    if (typeof refreshTimer.value === 'number') {
      clearTimeout(refreshTimer.value)
      refreshTimer.value = null
      return
    }
    if (refreshTimer.value.timeout) {
      clearTimeout(refreshTimer.value.timeout)
      refreshTimer.value.timeout = null
    }
    if (!refreshTimer.value.interval) {
      refreshTimer.value = null
    }
  }

  const setupAutoRefresh = () => {
    clearAutoRefresh()

    if (typeof window === 'undefined' || !isVerified.value || !verificationState.value) return

    try {
      const { timestamp } = verificationState.value
      const remaining = TURNSTILE_EXPIRY_TIME - (Date.now() - timestamp)
      const refreshTime = remaining * AUTO_REFRESH_THRESHOLD

      if (refreshTime > 0 && refreshTime < remaining) {
        if (!refreshTimer.value || typeof refreshTimer.value === 'number') {
          refreshTimer.value = {}
        }

        refreshTimer.value.timeout = setTimeout(() => {
          updateVerificationState()
          if (shouldAutoRefresh.value && autoRefreshCallback.value) {
            autoRefreshCallback.value()
          }
        }, refreshTime)
      }
    } catch (error) {
      console.error('Error setting up auto refresh:', error)
    }
  }

  const initAutoRefresh = () => {
    if (typeof window === 'undefined') return

    updateVerificationState()
    setupAutoRefresh()

    const handleStorageChange = (e) => {
      if (e.key === TURNSTILE_STORAGE_KEY || e.key === TURNSTILE_TOKEN_CACHE_KEY) {
        updateVerificationState()
        setupAutoRefresh()
      }
    }
    storageHandler.value = handleStorageChange
    window.addEventListener('storage', handleStorageChange)

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        updateVerificationState()
        setupAutoRefresh()
        if ((!isVerified.value || shouldAutoRefresh.value) && autoRefreshCallback.value) {
          autoRefreshCallback.value()
        }
      }
    }

    visibilityHandler.value = handleVisibilityChange
    document.addEventListener('visibilitychange', handleVisibilityChange)

    const stateCheckInterval = setInterval(() => {
      updateVerificationState()
    }, 30000)

    if (!refreshTimer.value || typeof refreshTimer.value === 'number') {
      refreshTimer.value = { interval: stateCheckInterval }
    } else {
      refreshTimer.value.interval = stateCheckInterval
    }
  }

  const cleanupAutoRefresh = () => {
    clearAutoRefresh()
    if (typeof window === 'undefined') return
    if (visibilityHandler.value) {
      document.removeEventListener('visibilitychange', visibilityHandler.value)
      visibilityHandler.value = null
    }
    if (storageHandler.value) {
      window.removeEventListener('storage', storageHandler.value)
      storageHandler.value = null
    }
    if (refreshTimer.value?.interval) {
      clearInterval(refreshTimer.value.interval)
    }
    refreshTimer.value = null
  }

  const refreshVerificationState = () => {
    updateVerificationState()
  }

  const openTurnstileModal = () => {
    modalVisible.value = true
  }

  const closeTurnstileModal = () => {
    modalVisible.value = false
  }

  /**
   * 请求前确保已通过 CF 校验；未通过则打开弹窗等待用户完成。
   * @param {string} path
   * @param {{ forceFresh?: boolean }} [options]
   *   forceFresh=true：忽略缓存并重新弹窗（注册发码每次；或 100094 重试）
   *   默认：有效缓存期内复用（匿名创建任务 30min）
   * @returns {Promise<boolean>}
   */
  const ensureTurnstileToken = async (path = '', options = {}) => {
    if (typeof window === 'undefined') return true
    if (path && !shouldRequireTurnstilePath(path)) return true

    if (options?.forceFresh) {
      clearVerification()
    } else {
      updateVerificationState()
    }

    if (isVerified.value && getTurnstileToken()) return true

    openTurnstileModal()

    return new Promise((resolve) => {
      pendingGateResolve = resolve
    })
  }

  /**
   * 匿名创建任务：进入「创建中」UI 前完成 CF；30 分钟缓存期内复用。
   */
  const ensureTurnstileBeforeAnonCreate = () =>
    ensureTurnstileToken('/create-task')

  /** 注册发验证码（含重发）：每次点击都强制重新 CF */
  const ensureTurnstileBeforeRegisterCode = () =>
    ensureTurnstileToken('/lip-sync/api/user/register', { forceFresh: true })
  const resolveTurnstileGate = (verified) => {
    closeTurnstileModal()
    if (typeof pendingGateResolve === 'function') {
      pendingGateResolve(Boolean(verified))
      pendingGateResolve = null
    }
  }

  return {
    isVerified,
    modalVisible,
    saveVerification,
    clearVerification,
    getTurnstileToken,
    getRemainingTime,
    getRemainingTimePercent,
    shouldAutoRefresh,
    setAutoRefreshCallback,
    initAutoRefresh,
    cleanupAutoRefresh,
    refreshVerificationState,
    openTurnstileModal,
    closeTurnstileModal,
    ensureTurnstileToken,
    ensureTurnstileBeforeAnonCreate,
    ensureTurnstileBeforeRegisterCode,
    resolveTurnstileGate,
    shouldRequireTurnstilePath,
    shouldSkipTurnstilePath,
    shouldRetryTurnstileByCode,
    getTurnstileHeaderName,
    TURNSTILE_SKIP_API_PATHS,
    TURNSTILE_TOKEN_CACHE_KEY,
    TURNSTILE_EXPIRY_TIME,
    TURNSTILE_RETRY_CODE,
  }
}
